/**
 * Стиль карты MapLibre и цвета слоя сот.
 *
 * Назначение файла: собрать стиль так, чтобы источник тайлов менялся одной переменной
 * окружения. По решению от 16.09.2026 на альфе используются свои региональные экстракты
 * OSM, а до их подъёма — публичные растровые тайлы OpenStreetMap
 * (docs/process/04-accounts-and-services.md §П4.2).
 *
 * Импорты: типы стиля из maplibre-gl.
 *
 * Правовая часть: строка правообладателя тайлов обязательна и не убирается —
 * это условие использования данных OpenStreetMap.
 */

import type { StyleSpecification } from "maplibre-gl";

/** Шаблон URL тайлов. Заменяется на свой сервер экстрактов через VITE_TILE_URL. */
const TILE_URL: string =
  import.meta.env["VITE_TILE_URL"] ?? "https://tile.openstreetmap.org/{z}/{x}/{y}.png";

/** Правообладатель тайлов. */
const TILE_ATTRIBUTION: string =
  import.meta.env["VITE_TILE_ATTRIBUTION"] ??
  '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>';

/** Цвета игры. Тёмная основа: улей живёт вечером, карта не должна слепить (docs/13-visual.md). */
export const COLORS = {
  background: "#0e1016",
  you: "#f2c14e",
  clan: "#e08a3c",
  ally: "#4ea3f2",
  enemy: "#d1495b",
  neutral: "#8d7ca6",
  free: "#3a4358",
  sanctuary: "#2f6f5e",
  hearthOutline: "#ffe08a",
  ringOutline: "#7fd1ae",
  hexOutline: "#1b2130",
  fogDark: "#05070c",
  fogSilhouette: "#141a29",
  fogEdge: "#2b3550",
} as const;

/**
 * Туман: цвет и плотность покрывала (docs/23-visual-and-feedback.md §23.2).
 *
 * Плотность близка к единице намеренно. Просвечивающий туман показывал улицы там,
 * где игрок не был, и ограничитель прогресса «ноги и туман» переставал работать.
 */
export const FOG_FILL_COLOR = [
  "match",
  ["get", "layer"],
  "silhouette",
  COLORS.fogSilhouette,
  COLORS.fogDark,
] as const;

export const FOG_FILL_OPACITY = [
  "match",
  ["get", "layer"],
  "silhouette",
  0.55,
  0.97,
] as const;

/**
 * Базовый стиль: растровые тайлы OSM, затемнённые под тёмную тему интерфейса.
 *
 * Затемнение делается на уровне слоя (raster-brightness), а не CSS-фильтром на канве:
 * фильтр canvas съедает производительность на телефоне, а на карте важна плавность.
 */
export function baseStyle(): StyleSpecification {
  return {
    version: 8,
    // Поле glyphs не задаётся: подписи на карте не рисуем, все надписи —
    // в интерфейсе поверх неё, поэтому шрифты карте не нужны
    sources: {
      osm: {
        type: "raster",
        tiles: [TILE_URL],
        tileSize: 256,
        maxzoom: 19,
        attribution: TILE_ATTRIBUTION,
      },
    },
    layers: [
      {
        id: "background",
        type: "background",
        paint: { "background-color": COLORS.background },
      },
      {
        id: "osm",
        type: "raster",
        source: "osm",
        paint: {
          "raster-opacity": 0.75,
          "raster-brightness-max": 0.65,
          "raster-saturation": -0.35,
          "raster-contrast": 0.1,
        },
      },
    ],
  };
}

/** Выражение раскраски сот по категории владения: одно место, где живут цвета карты. */
export const HEX_FILL_COLOR = [
  "match",
  ["get", "paint"],
  "you",
  COLORS.you,
  "clan",
  COLORS.clan,
  "ally",
  COLORS.ally,
  "enemy",
  COLORS.enemy,
  "neutral",
  COLORS.neutral,
  "sanctuary",
  COLORS.sanctuary,
  COLORS.free,
] as const;
